import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Link } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { UnifiedTodo } from '@/lib/todoHelpers';
import { TodoScopeSelector, type TodoScope } from '@/components/todos/TodoScopeSelector';

interface UnifiedTodoModalProps {
  isOpen: boolean;
  onClose: () => void;
  todo?: UnifiedTodo | null;
  onSave: () => Promise<void>;
  defaultLocationId?: string;
  defaultProjectId?: string;
  defaultOpportunityId?: string;
}

export function UnifiedTodoModal({ 
  isOpen, 
  onClose, 
  todo, 
  onSave,
  defaultLocationId,
  defaultProjectId,
  defaultOpportunityId 
}: UnifiedTodoModalProps) {
  const { user, userRole } = useAuth();
  const { toast } = useToast();
  const isChargingUser = userRole === 'charging_user';
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    location_id: defaultLocationId || '',
    project_id: defaultProjectId || '',
    opportunity_id: defaultOpportunityId || '',
    depends_on_todo_id: '',
    scope: (isChargingUser ? 'personal_charging' : 'personal_crm') as TodoScope,
    is_shared: false,
    status: 'open' as 'open' | 'in_progress' | 'completed' | 'cancelled' | 'blocked',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
  });
  
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  // Fetch locations for dropdown
  const { data: locations = [] } = useQuery({
    queryKey: ['charging-locations-for-todos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select('id, name, city')
        .is('deleted_at', null)
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch projects for dropdown (only for non-charging users)
  const { data: projects = [] } = useQuery({
    queryKey: ['projects-for-todos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .is('deleted_at', null)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !isChargingUser,
  });

  // Fetch available todos for dependency selection
  const { data: availableTodos = [] } = useQuery({
    queryKey: ['todos-for-dependency', formData.location_id, formData.project_id],
    queryFn: async () => {
      let query = supabase
        .from('unified_todos' as any)
        .select('id, title, status')
        .is('deleted_at', null);
      
      if (formData.location_id) {
        query = query.eq('location_id', formData.location_id);
      } else if (formData.project_id) {
        query = query.eq('project_id', formData.project_id);
      }
      
      // Don't show current todo in dependencies
      if (todo?.id) {
        query = query.neq('id', todo.id);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!(formData.location_id || formData.project_id),
    retry: 1,
  });

  // Fetch available users (filtered by role for charging users)
  const { data: availableUsers = [] } = useQuery({
    queryKey: ['available-users-for-todos', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .rpc('get_assignable_users', { requesting_user_id: user.id });

      if (error) throw error;
      return data || [];
    },
    enabled: isOpen && !!user?.id,
    retry: 1
  });

  useEffect(() => {
    if (todo) {
      // Use scope directly since migration already converted old values
      const mappedScope: TodoScope = todo.scope as TodoScope;
      
      setFormData({
        title: todo.title,
        description: todo.description || '',
        assigned_to: todo.assigned_to || '',
        location_id: todo.location_id || '',
        project_id: todo.project_id || '',
        opportunity_id: todo.opportunity_id || '',
        depends_on_todo_id: todo.depends_on_todo_id || '',
        scope: mappedScope,
        is_shared: todo.is_shared,
        status: todo.status,
        priority: todo.priority,
      });
      setDueDate(todo.due_date ? new Date(todo.due_date) : undefined);
    } else {
      setFormData({
        title: '',
        description: '',
        assigned_to: '',
        location_id: defaultLocationId || '',
        project_id: defaultProjectId || '',
        opportunity_id: defaultOpportunityId || '',
        depends_on_todo_id: '',
        scope: isChargingUser ? 'personal_charging' : 'personal_crm',
        is_shared: false,
        status: 'open',
        priority: 'medium',
      });
      setDueDate(undefined);
      
      // Verify table exists (development only)
      if (isOpen && import.meta.env.DEV) {
        import('@/lib/todoHelpers').then(({ verifyUnifiedTodosTable }) => {
          verifyUnifiedTodosTable().then(exists => {
            if (!exists) {
              console.error('[UnifiedTodoModal] CRITICAL: unified_todos table does not exist!');
              toast({
                title: 'Database configuratie fout',
                description: 'De unified_todos tabel bestaat niet. Neem contact op met support.',
                variant: 'destructive',
              });
            }
          });
        });
      }
    }
  }, [todo, isOpen, defaultLocationId, defaultProjectId, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    
    // Declare outside try block so it's accessible in catch
    let todoData: any;
    
    try {
      // Determine scope based on context
      let finalScope: TodoScope = formData.scope;
      if (formData.opportunity_id) finalScope = 'opportunity';
      else if (formData.location_id) finalScope = 'location';
      else if (formData.project_id) finalScope = 'project';

      todoData = {
        title: formData.title,
        description: formData.description || null,
        assigned_to: formData.assigned_to || null,
        created_by: user.id,
        location_id: formData.location_id || null,
        project_id: formData.project_id || null,
        opportunity_id: formData.opportunity_id || null,
        depends_on_todo_id: formData.depends_on_todo_id || null,
        scope: finalScope,
        is_shared: formData.is_shared,
        status: formData.status,
        priority: formData.priority,
        due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
      };

      if (todo) {
        const { error } = await supabase
          .from('unified_todos' as any)
          .update(todoData)
          .eq('id', todo.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('unified_todos' as any)
          .insert([todoData]);
        if (error) throw error;
      }

      toast({
        title: 'Succes',
        description: todo ? 'Taak bijgewerkt' : 'Taak aangemaakt',
      });

      await onSave();
      onClose();
    } catch (error: any) {
      console.error('[UnifiedTodoModal] Error saving todo:', {
        error: error,
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        stack: error.stack,
        todoData: todoData,
        userId: user?.id,
        userRole: userRole,
        currentPath: window.location.pathname
      });
      
      let errorMessage = 'Er ging iets mis bij het opslaan van de taak';
      
      // Tabel bestaat niet
      if (error.code === '42P01') {
        errorMessage = 'Database tabel niet gevonden. Neem contact op met support.';
        console.error('[CRITICAL] unified_todos table does not exist in database!');
      }
      // Permission denied
      else if (error.message?.includes('permission denied') || error.code === '42501') {
        errorMessage = 'Je hebt geen toegang om deze taak op te slaan. Controleer je rechten.';
        console.error('[RLS] Permission denied for user role:', userRole);
      }
      // Check constraint violation
      else if (error.message?.includes('violates check constraint')) {
        if (error.message?.includes('location_or_project_not_both')) {
          errorMessage = 'Ongeldige combinatie: taak kan niet aan zowel een locatie als project gekoppeld worden.';
        } else if (error.message?.includes('status')) {
          errorMessage = `Ongeldige status waarde: ${todoData.status}`;
        } else if (error.message?.includes('priority')) {
          errorMessage = `Ongeldige prioriteit waarde: ${todoData.priority}`;
        } else if (error.message?.includes('scope')) {
          errorMessage = `Ongeldige scope waarde: ${todoData.scope}`;
        } else {
          errorMessage = 'Ongeldige waarde ingevoerd. Controleer alle velden.';
        }
      }
      // Foreign key violation
      else if (error.message?.includes('foreign key') || error.code === '23503') {
        if (error.message?.includes('assigned_to')) {
          errorMessage = 'Geselecteerde gebruiker niet gevonden.';
        } else if (error.message?.includes('location_id')) {
          errorMessage = 'Geselecteerde locatie niet gevonden.';
        } else if (error.message?.includes('project_id')) {
          errorMessage = 'Geselecteerd project niet gevonden.';
        } else {
          errorMessage = 'Gekoppelde item niet gevonden. Probeer een ander item te selecteren.';
        }
      }
      // Not null violation
      else if (error.message?.includes('null value') || error.code === '23502') {
        const match = error.message?.match(/column "(\w+)"/);
        const column = match ? match[1] : 'unknown';
        errorMessage = `Verplicht veld ontbreekt: ${column}`;
      }
      // Charging user project restriction
      else if (error.message?.includes('charging_user') || error.message?.includes('project_id')) {
        errorMessage = 'Charging gebruikers kunnen alleen taken aan locaties koppelen, niet aan projecten.';
      }
      // Generic error with message
      else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Fout bij opslaan',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{todo ? 'Taak bewerken' : 'Nieuwe taak'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="assigned_to">Toegewezen aan</Label>
              <Select
                value={formData.assigned_to || '__none__'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, assigned_to: value === '__none__' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Niet toegewezen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Niet toegewezen</SelectItem>
                  {availableUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.display_name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="priority">Prioriteit</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Laag</SelectItem>
                  <SelectItem value="medium">Middel</SelectItem>
                  <SelectItem value="high">Hoog</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">Bezig</SelectItem>
                  <SelectItem value="completed">Voltooid</SelectItem>
                  <SelectItem value="blocked">Geblokkeerd</SelectItem>
                  <SelectItem value="cancelled">Geannuleerd</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Deadline</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, 'dd MMM yyyy', { locale: nl }) : 'Selecteer datum'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={setDueDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="border-t pt-4">
            <Label className="flex items-center gap-2 mb-3">
              <Link className="w-4 h-4" />
              Koppeling (optioneel)
            </Label>
            
            <div className={isChargingUser ? "" : "grid grid-cols-2 gap-4"}>
              <div>
                <Label htmlFor="location_id">Laadlocatie</Label>
                <Select
                  value={formData.location_id || '__none__'}
                  onValueChange={(value) => setFormData(prev => ({ 
                    ...prev, 
                    location_id: value === '__none__' ? '' : value,
                    project_id: (value && value !== '__none__') ? '' : prev.project_id
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Geen locatie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Geen locatie</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} ({loc.city})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!isChargingUser && (
                <div>
                  <Label htmlFor="project_id">CRM Project</Label>
                  <Select
                    value={formData.project_id || '__none__'}
                    onValueChange={(value) => setFormData(prev => ({ 
                      ...prev, 
                      project_id: value === '__none__' ? '' : value,
                      location_id: (value && value !== '__none__') ? '' : prev.location_id
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Geen project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Geen project</SelectItem>
                      {projects.map((proj) => (
                        <SelectItem key={proj.id} value={proj.id}>
                          {proj.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {(formData.location_id || formData.project_id) && availableTodos.length > 0 && (
            <div>
              <Label htmlFor="depends_on_todo_id">Afhankelijk van taak</Label>
              <Select
                value={formData.depends_on_todo_id || '__none__'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, depends_on_todo_id: value === '__none__' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Geen afhankelijkheid" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Geen afhankelijkheid</SelectItem>
                  {availableTodos.map((t: any) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.title} ({t.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Scope Selector - only show when no context is set */}
          {!formData.location_id && !formData.project_id && !formData.opportunity_id && (
            <TodoScopeSelector
              value={formData.scope}
              onChange={(scope) => setFormData(prev => ({ ...prev, scope }))}
              isChargingUser={isChargingUser}
              hasLocation={!!formData.location_id}
              hasProject={!!formData.project_id}
              hasOpportunity={!!formData.opportunity_id}
            />
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuleren
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Opslaan...' : todo ? 'Bijwerken' : 'Aanmaken'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
